import { Flame } from 'lucide-react'
import { CheckMark } from '@/components/CheckMark'
import { useStreak } from '@/hooks/useStreak'
import { cn } from '@/lib/utils'
import type { StreakStatus } from '@/lib/streak'

// Shared visual language for the streak across the three surfaces it appears on
// (Sidebar row, collapsed-sidebar corner badge, Dashboard stat). `active`/`at_risk`
// burn orange; a lapsed/absent streak is muted.
function flameClass(status: StreakStatus, count: number): string {
  if (status === 'inactive' || count <= 0) return 'text-muted-foreground'
  if (status === 'at_risk') return 'text-amber-500'
  return 'text-orange-500'
}

/**
 * Sidebar variant — a compact flame + count for the right side of the Dashboard
 * nav row (its `badge` slot). Renders nothing when there's no active streak, so
 * the row stays clean until the user has one.
 */
export function StreakNavBadge({ size = 'sm' }: { size?: 'sm' | 'md' }) {
  const { currentStreak, status, loading } = useStreak()
  if (loading || currentStreak <= 0) return null

  const color = flameClass(status, currentStreak)
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-semibold text-foreground tabular-nums',
        size === 'md' ? 'text-sm' : 'text-[11px]',
      )}
      title={
        status === 'at_risk'
          ? 'Study today to keep your streak alive'
          : `${currentStreak}-day study streak`
      }
    >
      <Flame className={`${size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5'} ${color} ${status === 'active' ? 'streak-flame-pulse' : ''}`} />
      {currentStreak} day{currentStreak === 1 ? '' : 's'}
    </span>
  )
}

/**
 * Corner variant — a compact badge to overlay on a nav icon, for the
 * collapsed (icon-only) sidebar where the row badge has nowhere to sit.
 * Renders nothing when there's no active streak, so the tab stays clean.
 * `className` overrides the corner it pins to, for icons that already carry a
 * badge on the top-right (the Quiz tab's questions-left count).
 */
export function StreakCornerBadge({ className }: { className?: string }) {
  const { currentStreak, loading } = useStreak()
  if (loading || currentStreak <= 0) return null
  return (
    <span className={cn(
      'absolute -top-1 -right-2 flex h-3.5 min-w-[0.875rem] items-center justify-center gap-0.5 rounded-full bg-orange-500 px-[3px] text-[9px] font-bold leading-none text-white tabular-nums',
      className,
    )}>
      {currentStreak}
    </span>
  )
}

/**
 * Dashboard variant — a flame stat tile matching the readiness/days tiles in the
 * header grid. When `activeToday` is true (at least one question has been
 * answered correctly today), the tile burns brighter with a filled-in flame and
 * a checkmark badge confirming today's streak activity is in. This is
 * independent of study-plan completion — see the Readiness tile's own
 * checkmark for that.
 */
export function StreakStat({
  activeToday = false,
}: {
  activeToday?: boolean
}) {
  const { currentStreak, longestStreak, status, loading } = useStreak()
  if (loading) return null

  const color = flameClass(status, currentStreak)
  return (
    <div
      className={`relative flex flex-col items-center justify-center gap-1.5 py-4 min-h-32 rounded-2xl transition-colors ${
        activeToday ? 'bg-orange-500/20 ring-1 ring-orange-500/40' : 'bg-orange-500/10'
      }`}
      title={
        currentStreak > 0
          ? `Longest streak: ${longestStreak} day${longestStreak === 1 ? '' : 's'}`
          : 'Answer a question correctly to start a streak'
      }
    >
      <span className="flex items-center gap-1 leading-none">
        <Flame
          className={`h-6 w-6 ${activeToday ? 'text-orange-500' : color} ${status === 'active' ? 'streak-flame-pulse' : ''}`}
          fill={activeToday ? 'currentColor' : 'none'}
        />
        <span className={`text-3xl font-bold tabular-nums ${activeToday ? 'text-orange-500' : currentStreak > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-muted-foreground'}`}>
          {currentStreak}
        </span>
      </span>
      <span className="text-xs text-muted-foreground">
        day streak{status === 'at_risk' ? ' · today?' : ''}
      </span>
      {activeToday && (
        <span
          className="absolute -top-1.5 -right-1.5 inline-flex rounded-full shadow ring-2 ring-background"
          title="Answered a question correctly today"
        >
          {/* The app's checkmark in the streak's own orange, not the "done"
              green: this marks the flame beside it, not a finished plan. */}
          <CheckMark
            className="block h-6 w-6 text-orange-500"
            label="Answered a question correctly today"
          />
        </span>
      )}
    </div>
  )
}
